import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { TagRead, tagFromText } from "../utils/readTag";

const FRAME_INTERVAL_MS = 250;

/** Camera plus the browser's own QR detection (Chrome on Android); stops at the first QR code. */
export function useQrScan(videoRef: RefObject<HTMLVideoElement | null>) {
  const supported =
    typeof window !== "undefined" && "BarcodeDetector" in window && !!navigator.mediaDevices?.getUserMedia;
  const [last, setLast] = useState<TagRead>();
  const [error, setError] = useState<string>();
  const [scanning, setScanning] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    setScanning(false);
  }, []);

  const start = useCallback(async () => {
    if (!supported || !window.BarcodeDetector || !videoRef.current) return;
    stop();
    setError(undefined);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();
      setScanning(true);
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      timerRef.current = window.setInterval(async () => {
        try {
          const codes = await detector.detect(video);
          const raw = codes[0]?.rawValue;
          if (raw === undefined) return;
          setLast({ tag: tagFromText(raw), via: "QR", raw: [raw] });
          stop();
        } catch {
          // A frame that could not be read; the next one is tried.
        }
      }, FRAME_INTERVAL_MS);
    } catch (e) {
      setError(e instanceof Error ? e.name : "cameraError");
      stop();
    }
  }, [stop, supported, videoRef]);

  useEffect(() => stop, [stop]);

  return { supported, scanning, start, stop, last, clear: () => setLast(undefined), error };
}
